import {
  FilterBarReducerAction,
  FilterBarActions,
  FilterBarAdditionPayload,
  FilterBarRemovalPayload,
} from '../types/filter-state.js';
import {
  FilterItemValues,
  FilterState,
  PinnedState,
} from '../types/shared-filter-types.js';

const checkForDuplicates = (state: FilterItemValues, name: string | number) => {
  if (state[name]) {
    throw new Error(
      'There are duplicated filter names, which will overwrite each other. Please use a unique name for each FilterBar.Item.',
    );
  }
};

/**
 * FilterBar reducer function used for managing the state of the filter items.
 * It keeps track of the currently applied filters and the pinned state of all filter items.
 * Both states are maintained independently of each other and can be synced with their own actions.
 */
export function reducer(
  state: FilterState,
  action: FilterBarReducerAction,
  hasControlledPinnedState: boolean,
  onPinnedStateChange?: (
    newState: PinnedState,
    changedItems: Array<string | number>,
  ) => void,
): FilterState {
  const { type, payload } = action;

  if (
    type === FilterBarActions.INITIALIZE ||
    type === FilterBarActions.CHANGE
  ) {
    const { name, value } = payload as FilterBarAdditionPayload;
    const newFilters: FilterItemValues = {
      ...state.filters,
      [name]: { value },
    };

    const newFilterState: FilterState = {
      ...state,
      filters: newFilters,
    };

    switch (type) {
      case FilterBarActions.INITIALIZE:
        state.filters && checkForDuplicates(state.filters, name);
        break;
      case FilterBarActions.CHANGE:
        break;
    }

    return newFilterState;
  }

  if (type === FilterBarActions.REMOVE) {
    const { name } = payload as FilterBarRemovalPayload;

    const newFilters: FilterItemValues = {
      ...state.filters,
    };
    delete newFilters[name];

    // used to reposition the overlay, because the displayValue of the Select is now always the same and removing filters
    // doesn't trigger the resize observer to update the popper overlay anymore
    window.dispatchEvent(new Event('resize'));

    return {
      filters: newFilters,
      itemsPinnedState: state.itemsPinnedState,
    };
  }

  if (type === FilterBarActions.CHANGEPINNEDSTATE) {
    const { newPinnedState } = payload;

    return {
      filters: state.filters,
      itemsPinnedState: newPinnedState,
    };
  }

  if (type === FilterBarActions.UNPIN || type === FilterBarActions.PIN) {
    const newPinnedStateValue =
      type === FilterBarActions.PIN ? 'pinned-optional' : 'optional';

    const { keys } = payload;
    const newPinnedState: PinnedState = {
      ...state.itemsPinnedState,
    };

    keys?.forEach((key) => {
      newPinnedState[key] = newPinnedStateValue;
    });

    requestAnimationFrame(() => onPinnedStateChange?.(newPinnedState, keys));

    if (hasControlledPinnedState) {
      return state;
    }

    return {
      filters: state.filters,
      itemsPinnedState: newPinnedState,
    };
  }

  throw new Error(`Unknown action type: ${type}`);
}
